<script setup lang="ts">
import { computed, ref } from 'vue';
import Avatar from 'primevue/avatar';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { useUtilities } from '@apparatix/composables';
import { Noir } from '@apparatix/theme/Noir';
import { Toolbar, Typography } from '@apparatix/library/core';
import type { RelationshipType } from '@apparatix/schemas/Relationship';
import { SectionIconography } from './SectionIconography';

type Props = {
  type: RelationshipType,
  menuItems?: MenuItem[],
  /**
   * By default, the hamburger menu is only shown on mobile.
   * Setting this prop to true will make it appear on desktop
   */
  forceMenuButtonOnDesktop?: boolean,
};

const props = defineProps<Props>();

const { arrayHasValues } = useUtilities();

const menu = ref();
const menuId = ref(crypto.randomUUID());

const avatarProps = computed(() => {
  const config = SectionIconography[props.type];

  return {
    icon: config.icon,
    style: {
      backgroundColor: config.backgroundColor,
      color: config.color,
    },
  };
});

const onMenuButtonclick = (e: MouseEvent) => menu.value.toggle(e);
</script>
<template>
  <div class="flex justify-between p-4">
    <div class="flex items-center gap-3">
      <div class="hidden lg:block">
        <Avatar shape="circle" size="xlarge" v-bind="avatarProps" />
      </div>
      <div class="lg:hidden">
        <Avatar shape="circle" size="large" v-bind="avatarProps" />
      </div>
      <div>
        <Typography variant="drawer-title">
          <slot name="title" />
        </Typography>
        <Typography variant="drawer-subtitle">
          <slot name="subtitle" />
        </Typography>
      </div>
    </div>
    <Toolbar class="hidden lg:flex">
      <slot name="buttons" />
      <Button
        v-if="props.forceMenuButtonOnDesktop"
        icon="pi pi-bars"
        variant="text"
        aria-label="Open menu"
        aria-haspopup="true"
        :aria-controls="menuId"
        :dt="Noir.button"
        rounded
        @click="onMenuButtonclick"
      />
    </Toolbar>
    <Toolbar v-if="arrayHasValues(props.menuItems)" class="flex lg:hidden">
      <Button
        icon="pi pi-bars"
        variant="text"
        aria-label="Open menu"
        aria-haspopup="true"
        :aria-controls="menuId"
        :dt="Noir.button"
        rounded
        @click="onMenuButtonclick"
      />
    </Toolbar>
  </div>
  <Menu
    :id="menuId"
    ref="menu"
    :model="props.menuItems"
    popup
  />
</template>
